/**
 * Marquee Component – Dawn Theme
 *
 * Vanilla Web Component ohne Fabric-Abhängigkeiten (@theme/component, @theme/utilities).
 * Portiert von fabric_smartful_v2 marquee.js.
 *
 * Features:
 *  - Automatisches Auffüllen des sichtbaren Bereichs mit Kopien der Items
 *  - Geschwindigkeit skaliert mit sqrt(Anzahl Kopien) × Speed-Faktor
 *  - Verlangsamung bei Hover (smooth via requestAnimationFrame)
 *  - Resize-Handler (debounced)
 *  - Respektiert prefers-reduced-motion (CSS übernimmt das)
 */

const ANIMATION_OPTIONS = { duration: 500 };

class MarqueeComponent extends HTMLElement {
  /** @type {{ cancel: () => void; current: number } | null} */
  #animation = null;

  /** @type {number | null} */
  #marqueeWidth = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #resizeTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #slowdownTimer = null;

  // ── DOM refs ──────────────────────────────────
  get #wrapper()   { return this.querySelector('[data-marquee-wrapper]'); }
  get #content()   { return this.querySelector('[data-marquee-content]'); }
  get #itemsEl()   { return this.querySelector('[data-marquee-items]'); }
  get #clonedContent() {
    const last = this.#wrapper?.lastElementChild;
    return last !== this.#content ? last : null;
  }

  // ── Lifecycle ──────────────────────────────────
  connectedCallback() {
    if (!this.#itemsEl) return;
    this.#init();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
  }

  // ── Init ──────────────────────────────────
  async #init() {
    const { numberOfCopies } = await this.#queryNumberOfCopies();
    const speed = this.#calcSpeed(numberOfCopies);
    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);

    window.addEventListener('resize', this.#onResize);
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
  }

  // ── Speed ──────────────────────────────────
  #setSpeed(value) {
    // value = seconds; CSS variable drives the animation-duration
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calcSpeed(numberOfCopies) {
    const factor = Number(this.dataset.speed) || 30;
    return Math.sqrt(numberOfCopies) * factor;
  }

  // ── Copy management ───────────────────────────────
  async #queryNumberOfCopies() {
    if (!this.#itemsEl) {
      return { numberOfCopies: 1 };
    }

    const viewportW  = this.getBoundingClientRect().width;
    const itemsW     = this.#itemsEl.getBoundingClientRect().width;
    const isHResize  = this.#marqueeWidth !== viewportW;
    this.#marqueeWidth = viewportW;

    return {
      numberOfCopies: itemsW === 0 ? 1 : Math.ceil(viewportW / itemsW),
      isHorizontalResize: isHResize,
    };
  }

  #addRepeatedItems(numberOfCopies) {
    const content  = this.#content;
    const itemsEl  = this.#itemsEl;
    if (!itemsEl || !content) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = itemsEl.cloneNode(true);
      clone.removeAttribute('data-marquee-items');
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(count) {
    const content  = this.#content;
    if (!content) return;
    const children = Array.from(content.children);
    const toRemove = Math.min(count, children.length - 1);
    for (let i = 0; i < toRemove; i++) {
      content.lastElementChild?.remove();
    }
  }

  #duplicateContent() {
    this.#clonedContent?.remove();
    const clone = this.#content?.cloneNode(true);
    if (!clone || !this.#wrapper) return;
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-marquee-content');
    this.#wrapper.appendChild(clone);
  }

  // ── Hover slowdown ───────────────────────────────
  #onPointerEnter = () => {
    // debounce: only slow down if pointer stays > 500ms
    this.#slowdownTimer = setTimeout(() => {
      const anim = this.#wrapper?.getAnimations()[0];
      if (!anim || this.#animation) return;

      this.#animation = animateValue({
        ...ANIMATION_OPTIONS,
        from: 1,
        to: 0.15,
        onUpdate: (v) => anim.updatePlaybackRate(v),
        onComplete: () => { this.#animation = null; },
      });
    }, ANIMATION_OPTIONS.duration);
  };

  #onPointerLeave = () => {
    clearTimeout(this.#slowdownTimer);
    const anim = this.#wrapper?.getAnimations()[0];
    if (!anim) return;

    const from = this.#animation?.current ?? anim.playbackRate;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (v) => anim.updatePlaybackRate(v),
      onComplete: () => { this.#animation = null; },
    });
  };

  // ── Resize ───────────────────────────────
  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(async () => {
      const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();
      if (!isHorizontalResize) return;

      const current = this.#content?.children.length ?? 1;
      const speed   = this.#calcSpeed(numberOfCopies);

      if (numberOfCopies > current) {
        this.#addRepeatedItems(numberOfCopies - current);
      } else if (numberOfCopies < current) {
        this.#removeRepeatedItems(current - numberOfCopies);
      }

      this.#duplicateContent();
      this.#setSpeed(speed);

      // Restart animation from 0
      requestAnimationFrame(() => {
        this.#wrapper?.getAnimations().forEach((a) => { a.currentTime = 0; });
      });
    }, 250);
  };
}

// ── Utility: smooth value animation ──────────────────────────
function animateValue({ from, to, duration, onUpdate, onComplete, easing = (t) => t * t * (3 - 2 * t) }) {
  const start = performance.now();
  let cancelled = false;
  let current = from;

  function tick(now) {
    if (cancelled) return;
    const progress = Math.min((now - start) / duration, 1);
    current = from + (to - from) * easing(progress);
    onUpdate(current);
    if (progress < 1) requestAnimationFrame(tick);
    else if (typeof onComplete === 'function') onComplete();
  }

  requestAnimationFrame(tick);

  return {
    get current() { return current; },
    cancel() { cancelled = true; },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}